/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  DatePicker,
} from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';

import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_EN_PROCESO,
  ESTATUS_INACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_CANCELADO,
  ESTADO_ACTIVO,
} from 'utils/constants';
import { deleteJustificacionReloj, getJustificacionesOptimizedReloj } from 'api/configuraciones/clock/Justificacion/Justificaciones';

// Se autorizan o rechazan las justificaciones Activas y las En proceso: las que envía el
// empleado desde el Portal del Empleado llegan En proceso (pendientes de RH)
const POR_RESOLVER = [ESTATUS_ACTIVO, ESTATUS_EN_PROCESO];

const JustificacionGeneralReloj = ({
  permission, changeToForms, data_to_search,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [isFirst, setIsFirst] = useState(true);
  const [disableFields, setDisableFields] = useState(false);
  const [pageActual, setPageActual] = useState(1);

  const fetchData = async (argumentToSearch) => {
    const _data = await getJustificacionesOptimizedReloj({ ...argumentToSearch });
    setData(_data);
  };

  useEffect(() => {
    if (isFirst) {
      setIsFirst(false); // skip on first render
      return;
    }
    fetchData(data_to_search);
  }, [data_to_search]);

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
      ] = await Promise.all([
        getEstadosGlobales(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };

  const onAut_Rechazar = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    changeToForms(key, true);
    setLoading(false);
  };

  const onClickEdit = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    changeToForms(key, true);
    setLoading(false);
  };

  const onClickDelete = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteJustificacionReloj(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData(data_to_search);
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
  };

  const Justificacion = {
    1: 'Sin Justificación',
    4: 'Autorizada',
    6: 'Rechazado',
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Fecha',
      dataIndex: 'fecha_carga_reporte',
      key: 'fecha_carga_reporte',
      width: 100,
    },
    {
      titleText: 'Día',
      dataIndex: 'dia_de_la_semana',
      key: 'dia_de_la_semana',
      width: 100,
    },
    {
      titleText: 'Horario Jornada Entrada',
      dataIndex: 'horario_jornada_entrada',
      key: 'horario_jornada_entrada',
      width: 200,
    },
    {
      titleText: 'Horario Jornada Salida',
      dataIndex: 'horario_jornada_salida',
      key: 'horario_jornada_salida',
      width: 190,
    },
    {
      titleText: 'Hora de Entrada',
      dataIndex: 'hora_entrada',
      key: 'hora_entrada',
      width: 150,
    },
    {
      titleText: 'Hora de Salida',
      dataIndex: 'hora_salida',
      key: 'hora_salida',
      width: 150,
    },
    {
      titleText: 'Evaluación',
      dataIndex: 'tipo_de_evaluacion_de_asistencia_nombre',
      key: 'tipo_de_evaluacion_de_asistencia_nombre',
      width: 190,
    },
    {
      titleText: 'Justificación',
      dataIndex: 'estados_globales_id',
      key: 'estados_globales_id',
      width: 160,
      render: (value) => Justificacion[value],
    },
    {
      titleText: 'No. de Oficio/Fecha',
      dataIndex: 'no_oficio_fecha',
      key: 'no_oficio_fecha',
      width: 190,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          mobileCols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          permission={permission}
          allowExportPDF={false}
          controls={{
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          customActions={
            [
              {
                onClick: () => onAut_Rechazar(),
                icon: <CheckCircleOutlined />,
                text: 'Autorizar/Rechazar',
                disabledBySelection: true,
                disabled: !POR_RESOLVER.includes(selectedData?.estados_globales_id),
              },
            ]
          }
        />
      </Spin>
    </Row>
  );
};

export default JustificacionGeneralReloj;
